import type { Component } from 'vue';

/** Key of a tree node; compared with `===`. */
export type NxTreeKey = string | number;

/**
 * One node of an `NxTreeView`. `data` carries whatever the caller needs back
 * (a mailbox, a page, a layout node) — the tree never reads it.
 */
export interface NxTreeNode<T = unknown> {
  key: NxTreeKey;
  label: string;
  /** Loaded children. Leave out (and set `hasChildren`) for lazy loading. */
  children?: NxTreeNode<T>[];
  /** Has children that `loadChildren` fetches on first expand. */
  hasChildren?: boolean;
  icon?: Component;
  /**
   * A count shown as a muted pill (unread mails, entries) — a plain number in
   * `density="compact"`; 0 hides it.
   */
  count?: number;
  /**
   * What the count means, in words („3 ungelesen“): the count's tooltip, and
   * read instead of the bare number (as NxNavItem's `countLabel`).
   */
  countLabel?: string;
  /**
   * Draws the label strong — a folder that holds unread mails. In
   * `density="compact"` its count takes the brand colour too.
   */
  emphasis?: boolean;
  /** Focusable but not selectable, not draggable and no drop target. */
  disabled?: boolean;
  /**
   * Set `false` for a container node (a group heading, a folder you may not
   * open): it looks like any other row, but a click, Enter or Space opens or
   * closes it instead of selecting it. It is never selected, never emits
   * `select` or `activate` and carries no `aria-selected`; keyboard focus,
   * dragging and dropping stay as for any row (veto those with `draggable`,
   * `canDrop` and `canDropExternal`). Defaults to `true`.
   */
  selectable?: boolean;
  /** Set `false` to keep a node from being dragged while the tree allows it. */
  draggable?: boolean;
  data?: T;
}

/** Whether a node can be selected: neither `disabled` nor `selectable: false`. */
export function isSelectableNode<T>(node: NxTreeNode<T>): boolean {
  return !node.disabled && node.selectable !== false;
}

/** Where a dragged node lands relative to the target row. */
export type NxTreeDropPosition = 'before' | 'inside' | 'after';

export interface NxTreeMove<T = unknown> {
  source: NxTreeNode<T>;
  target: NxTreeNode<T>;
  position: NxTreeDropPosition;
}

/** One visible row of the flattened tree. */
export interface NxTreeRow<T = unknown> {
  node: NxTreeNode<T>;
  /** 1-based, as `aria-level`. */
  level: number;
  parentKey: NxTreeKey | null;
  posinset: number;
  setsize: number;
  /** The node has (or may lazily have) children. */
  expandable: boolean;
  expanded: boolean;
}

/** Children of a node: its own, else those `loadChildren` fetched. */
export function childrenOf<T>(
  node: NxTreeNode<T>,
  loaded: ReadonlyMap<NxTreeKey, NxTreeNode<T>[]>
): NxTreeNode<T>[] | undefined {
  return node.children ?? loaded.get(node.key);
}

function isExpandable<T>(node: NxTreeNode<T>, loaded: ReadonlyMap<NxTreeKey, NxTreeNode<T>[]>) {
  const kids = childrenOf(node, loaded);
  return kids ? kids.length > 0 : Boolean(node.hasChildren);
}

/** Case-insensitive label match. */
export function matchesFilter(label: string, query: string): boolean {
  return label.toLowerCase().includes(query.toLowerCase());
}

/**
 * Keys that stay visible for a filter: every match and all its ancestors.
 * Only loaded children are searched. `null` when there is no filter.
 */
export function filterKeys<T>(
  nodes: readonly NxTreeNode<T>[],
  query: string,
  loaded: ReadonlyMap<NxTreeKey, NxTreeNode<T>[]>
): Set<NxTreeKey> | null {
  const q = query.trim();
  if (!q) return null;
  const keep = new Set<NxTreeKey>();
  const walk = (list: readonly NxTreeNode<T>[]): boolean => {
    let any = false;
    for (const node of list) {
      const kids = childrenOf(node, loaded);
      const below = kids ? walk(kids) : false;
      if (below || matchesFilter(node.label, q)) {
        keep.add(node.key);
        any = true;
      }
    }
    return any;
  };
  walk(nodes);
  return keep;
}

/**
 * The visible rows in document order. With a filter, only kept nodes show and
 * every kept node with kept children is open, whatever `expanded` says.
 */
export function flattenTree<T>(
  nodes: readonly NxTreeNode<T>[],
  expanded: ReadonlySet<NxTreeKey>,
  loaded: ReadonlyMap<NxTreeKey, NxTreeNode<T>[]>,
  keep: ReadonlySet<NxTreeKey> | null
): NxTreeRow<T>[] {
  const rows: NxTreeRow<T>[] = [];
  const walk = (list: readonly NxTreeNode<T>[], level: number, parentKey: NxTreeKey | null) => {
    const shown = keep ? list.filter((n) => keep.has(n.key)) : list;
    shown.forEach((node, i) => {
      const kids = childrenOf(node, loaded);
      const keptKids = keep && kids ? kids.some((k) => keep.has(k.key)) : false;
      const open = keep ? keptKids : expanded.has(node.key);
      rows.push({
        node,
        level,
        parentKey,
        posinset: i + 1,
        setsize: shown.length,
        expandable: isExpandable(node, loaded),
        expanded: open && isExpandable(node, loaded),
      });
      if (open && kids) walk(kids, level + 1, node.key);
    });
  };
  walk(nodes, 1, null);
  return rows;
}

/** Whether `key` is `ancestor` itself or lies below it. */
export function isWithin<T>(
  ancestor: NxTreeNode<T>,
  key: NxTreeKey,
  loaded: ReadonlyMap<NxTreeKey, NxTreeNode<T>[]>
): boolean {
  if (ancestor.key === key) return true;
  const kids = childrenOf(ancestor, loaded) ?? [];
  return kids.some((k) => isWithin(k, key, loaded));
}

/** Splits a label around the filter matches, for highlighting. */
export function highlightParts(label: string, query: string): { text: string; hit: boolean }[] {
  const q = query.trim().toLowerCase();
  if (!q) return [{ text: label, hit: false }];
  const parts: { text: string; hit: boolean }[] = [];
  const lower = label.toLowerCase();
  let at = 0;
  for (;;) {
    const i = lower.indexOf(q, at);
    if (i < 0) break;
    if (i > at) parts.push({ text: label.slice(at, i), hit: false });
    parts.push({ text: label.slice(i, i + q.length), hit: true });
    at = i + q.length;
  }
  if (at < label.length) parts.push({ text: label.slice(at), hit: false });
  return parts;
}
